import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Tag, Modal, message, Tooltip, Switch, Alert } from 'antd';
import { CopyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import './callbackAddress.less';
import {
  getCallbackAddressList,
  saveCallbackAdmin,
  saveCallbackAddress,
  clearCallbackAddress,
  testCallbackAddress,
  resetCallbackSecret,
  resetCallbackAppId,
  getDeveloperAccounts,
} from '../../../api';
import { useRequest } from 'ahooks';
import { useRecoilValue } from 'recoil';
import { userInfo as recoilUserInfo } from '../../../recoil';
import { i18n as i, getLocale } from '../../../i18n';
import { generateConfig } from '../../../gs-utils/core/utils';
import { ERROR_CODE_MAP } from '../../../constants/errorCode';
import InfiniteScrollSelect, { InfiniteScrollSelectLoadParams } from '../../../components/InfiniteScrollSelect';

const LANGUAGE_OPTIONS = [
  { label: '简体中文', value: 'zh-CN' },
  { label: 'English', value: 'en-US' },
  { label: '日本語', value: 'ja-JP' },
  { label: 'Deutsch', value: 'de-DE' },
];

/** messageType: 1=事件 2=任务 */
const MSG_TYPE_INCIDENT: CallbackAddressType.MessageType = 1;
const MSG_TYPE_TASK: CallbackAddressType.MessageType = 2;

function CallbackAddress() {
  const userInfo = useRecoilValue(recoilUserInfo);
  const currentPersonId = userInfo?.person?.id || '';

  // ========== 数据 ==========
  const [listData, setListData] = useState<CallbackAddressType.CallbackAddressListRes | null>(null);
  const [accounts, setAccounts] = useState<DeveloperAccountType.DeveloperAccount[]>([]);
  const [accountTotal, setAccountTotal] = useState(0);

  // ========== 推送账号 ==========
  const [adminDraft, setAdminDraft] = useState('');
  const [adminSaving, setAdminSaving] = useState(false);

  // ========== 卡片编辑 ==========
  const [editingType, setEditingType] = useState<CallbackAddressType.MessageType | null>(null);
  const [drafts, setDrafts] = useState({
    incidentUrl: '',
    incidentLang: '',
    incidentEnabled: 0,
    taskUrl: '',
    taskEnabled: 0,
  });
  const [saving, setSaving] = useState(false);

  // ========== 测试 ==========
  const [testingType, setTestingType] = useState<CallbackAddressType.MessageType | null>(null);
  const [testResults, setTestResults] = useState<Record<number, { ok: boolean; msg: string } | null>>({});

  // ========== 凭证（首次签发后的临时明文 Secret） ==========
  const [justIssuedSecrets, setJustIssuedSecrets] = useState<Record<number, string>>({});

  // ========== 管理凭证弹窗 ==========
  const [manageModalType, setManageModalType] = useState<CallbackAddressType.MessageType | null>(null);
  const [resetAppIdResult, setResetAppIdResult] = useState<string | null>(null);
  const [resetSecretResult, setResetSecretResult] = useState<string | null>(null);
  const [resettingAppId, setResettingAppId] = useState(false);
  const [resettingSecret, setResettingSecret] = useState(false);

  // ========== 数据加载 ==========
  const applyList = (res: CallbackAddressType.ApiResponse<CallbackAddressType.CallbackAddressListRes>) => {
    const data = res?.data;
    if (!data) return;
    setListData(data);
    setAdminDraft(data.admin?.personId || '');
    setDrafts({
      incidentUrl: data.incident?.callbackUrl || '',
      incidentLang: data.incident?.incidentLanguage || '',
      incidentEnabled: data.incident?.callbackEnabled ?? 0,
      taskUrl: data.task?.callbackUrl || '',
      taskEnabled: data.task?.callbackEnabled ?? 0,
    });
    // 确保当前已设置的推送账号在下拉选项中存在，避免 Select 回显 UUID
    if (data.admin?.personId) {
      setAccounts((prev) => {
        const exists = prev.some((a) => a.personId === data.admin.personId);
        if (!exists) {
          return [
            {
              personId: data.admin.personId,
              nickname: data.admin.nickname || '',
              account: data.admin.account || '',
            } as DeveloperAccountType.DeveloperAccount,
            ...prev,
          ];
        }
        return prev;
      });
    }
  };

  const { run: runGetList } = useRequest(getCallbackAddressList, {
    manual: true,
    onSuccess: applyList,
  });

  const loadDevelopers = async (params: InfiniteScrollSelectLoadParams) => {
    try {
      const res = await getDeveloperAccounts({
        page: params.page,
        pageSize: params.pageSize,
        keyword: params.searchValue,
      });
      const developers = res?.data?.developers || [];
      const total = res?.data?.total || 0;
      setAccountTotal(total);
      setAccounts((prev) => (params.page === 1 ? developers : [...prev, ...developers]));
    } catch (e) {
      message.error(i('获取开发者账号失败'));
    }
  };

  useEffect(() => {
    if (currentPersonId) runGetList();
  }, [currentPersonId]);

  // ========== 工具函数 ==========
  const admin = listData?.admin;
  const incident = listData?.incident;
  const task = listData?.task;
  const canEdit = admin?.canEdit ?? true;
  const canChangeAdmin = admin?.canChangeAdmin ?? true;
  const adminIsSet = !!admin?.personId;

  const getConfig = (type: CallbackAddressType.MessageType) =>
    type === MSG_TYPE_INCIDENT ? incident : task;

  const isRegistered = (type: CallbackAddressType.MessageType) =>
    !!getConfig(type)?.registered;

  const isCardEditing = (type: CallbackAddressType.MessageType) =>
    editingType === type;

  const isCardEditable = (type: CallbackAddressType.MessageType) =>
    isCardEditing(type) || !isRegistered(type);

  const setField = (field: string, value: string | number) => {
    setDrafts((d) => ({ ...d, [field]: value }));
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(i('复制成功'));
    } catch {
      message.error(i('复制失败，请手动选择'));
    }
  };

  const cardLabel = (type: CallbackAddressType.MessageType) =>
    type === MSG_TYPE_INCIDENT ? i('事件回调地址') : i('任务报告回调地址');

  // ========== 推送账号操作 ==========
  const handleSaveAdmin = () => {
    if (!adminDraft) {
      message.error(i('请选择推送账号'));
      return;
    }
    const selectedAccount = accounts.find(a => a.personId === adminDraft);
    const displayName = selectedAccount?.nickname || selectedAccount?.account || adminDraft;
    Modal.confirm({
      title: i('保存推送账号'),
      icon: <ExclamationCircleOutlined />,
      content: i('保存推送账号确认', { name: displayName }),
      okText: i('确认保存'),
      cancelText: i('取消'),
      onOk: async () => {
        setAdminSaving(true);
        try {
          const res = await saveCallbackAdmin({ adminPersonId: adminDraft });
          if (res?.code === 0) {
            message.success(i('保存成功'));
            runGetList();
          } else {
            message.error(ERROR_CODE_MAP[res?.code] || i('保存失败'));
          }
        } catch (e) {
          message.error(i('保存失败'));
        } finally {
          setAdminSaving(false);
        }
      },
    });
  };

  // ========== 卡片编辑操作 ==========
  const handleModify = (type: CallbackAddressType.MessageType) => {
    setEditingType(type);
    setTestResults((prev) => ({ ...prev, [type]: null }));
    const config = getConfig(type);
    if (type === MSG_TYPE_INCIDENT) {
      setDrafts((d) => ({
        ...d,
        incidentUrl: config?.callbackUrl || '',
        incidentLang: config?.incidentLanguage || '',
        incidentEnabled: config?.callbackEnabled ?? 0,
      }));
    } else {
      setDrafts((d) => ({
        ...d,
        taskUrl: config?.callbackUrl || '',
        taskEnabled: config?.callbackEnabled ?? 0,
      }));
    }
  };

  const handleCancel = () => {
    setEditingType(null);
    setTestResults({});
    if (listData) {
      setDrafts({
        incidentUrl: listData.incident?.callbackUrl || '',
        incidentLang: listData.incident?.incidentLanguage || '',
        incidentEnabled: listData.incident?.callbackEnabled ?? 0,
        taskUrl: listData.task?.callbackUrl || '',
        taskEnabled: listData.task?.callbackEnabled ?? 0,
      });
    }
  };

  const doSave = async (type: CallbackAddressType.MessageType) => {
    const isEvent = type === MSG_TYPE_INCIDENT;
    const callbackUrl = isEvent ? drafts.incidentUrl : drafts.taskUrl;
    const callbackEnabled = isEvent ? drafts.incidentEnabled : drafts.taskEnabled;
    setSaving(true);
    try {
      const config = getConfig(type);
      const isFirstSave = !config?.callbackAppId;
      const res = await saveCallbackAddress({
        messageType: type,
        callbackUrl: callbackUrl.trim(),
        incidentLanguage: isEvent && drafts.incidentLang ? drafts.incidentLang : undefined,
        callbackEnabled,
      });
      if (res?.code !== 0) {
        message.error(ERROR_CODE_MAP[res?.code] || i('保存失败'));
        return;
      }
      message.success(i('保存成功'));
      setEditingType(null);
      setTestResults((prev) => ({ ...prev, [type]: null }));
      // 刷新列表，首次保存后需要展示新签发的凭证
      const listRes = await getCallbackAddressList();
      applyList(listRes);
      // 首次签发 → 查一下新的 AppId，并弹出签发提示
      if (isFirstSave) {
        const newConfig = type === MSG_TYPE_INCIDENT ? listRes?.data?.incident : listRes?.data?.task;
        showIssuedModal(type, newConfig?.callbackAppId || '');
      }
    } catch (e) {
      message.error(i('保存失败'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (type: CallbackAddressType.MessageType) => {
    const isEvent = type === MSG_TYPE_INCIDENT;
    const callbackUrl = isEvent ? drafts.incidentUrl : drafts.taskUrl;

    // 校验放在弹窗之前，不满足条件直接 toast 提示
    if (!callbackUrl?.trim()) {
      message.error(i('请填写回调地址'));
      return;
    }
    if (!adminIsSet) {
      message.error(i('请先指定推送账号'));
      return;
    }

    const config = getConfig(type);
    const isFirstSave = !config?.callbackAppId;
    const callbackEnabled = isEvent ? drafts.incidentEnabled : drafts.taskEnabled;
    const willReEnablePush = config?.disabledBySystem && callbackEnabled === 1;

    let content = i('确认保存回调地址提示');
    if (isEvent) {
      content = i('确认保存事件回调地址提示');
    }
    if (isFirstSave) {
      content += '\n' + i('首次保存签发凭证提示');
    }
    if (willReEnablePush) {
      content += '\n' + i('保存将恢复推送提示');
    }

    Modal.confirm({
      title: i('确认保存'),
      icon: <ExclamationCircleOutlined />,
      content,
      okText: i('确认保存'),
      cancelText: i('取消'),
      onOk: () => doSave(type),
    });
  };

  const handleClear = (type: CallbackAddressType.MessageType) => {
    Modal.confirm({
      title: i('确认清空'),
      icon: <ExclamationCircleOutlined />,
      content: i('清空回调地址确认提示'),
      okText: i('确认清空'),
      cancelText: i('取消'),
      onOk: async () => {
        try {
          const res = await clearCallbackAddress({ messageType: type });
          if (res?.code === 0) {
            message.success(i('清空成功'));
            setEditingType(null);
            setTestResults((prev) => ({ ...prev, [type]: null }));
            runGetList();
          } else {
            message.error(ERROR_CODE_MAP[res?.code] || i('清空失败'));
          }
        } catch (e) {
          message.error(i('清空失败'));
        }
      },
    });
  };

  // ========== 测试 ==========
  const handleTest = async (type: CallbackAddressType.MessageType) => {
    const isEvent = type === MSG_TYPE_INCIDENT;
    const url = isEvent ? drafts.incidentUrl : drafts.taskUrl;
    if (!url?.trim()) {
      setTestResults((prev) => ({
        ...prev,
        [type]: { ok: false, msg: i('请先填写回调地址') },
      }));
      return;
    }
    setTestingType(type);
    setTestResults((prev) => ({
      ...prev,
      [type]: { ok: true, msg: i('正在发送探测请求') },
    }));
    try {
      const res = await testCallbackAddress({ messageType: type, callbackUrl: url.trim() });
      if (res?.code === 0) {
        const { reachable, httpCode } = res.data || {};
        if (reachable) {
          setTestResults((prev) => ({
            ...prev,
            [type]: { ok: true, msg: i('测试通过提示') },
          }));
        } else {
          setTestResults((prev) => ({
            ...prev,
            [type]: { ok: false, msg: i('测试不可达提示', { httpCode: httpCode || 0 }) },
          }));
        }
      } else {
        setTestResults((prev) => ({
          ...prev,
          [type]: { ok: false, msg: ERROR_CODE_MAP[res?.code] || i('测试失败提示') },
        }));
      }
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [type]: { ok: false, msg: i('测试失败提示') },
      }));
    } finally {
      setTestingType(null);
    }
  };

  // ========== 首次签发弹窗 ==========
  const showIssuedModal = (type: CallbackAddressType.MessageType, appId: string) => {
    // 首次保存后 Secret 需要通过重置接口获取
    // 直接调用 resetSecret 来获取首次签发的 Secret
    resetCallbackSecret({ messageType: type }).then((res) => {
      if (res?.code !== 0) {
        message.error(ERROR_CODE_MAP[res?.code] || i('获取Secret失败'));
        return;
      }
      const secret = res?.data?.callbackSecret || '';
      setJustIssuedSecrets((prev) => ({ ...prev, [type]: secret }));
      Modal.info({
        title: i('已签发凭证标题', { type: cardLabel(type) }),
        width: 520,
        icon: null,
        content: (
          <div className="cb-issued-modal">
            <p className="cb-issued-tip">{i('签发凭证提示文案')}</p>
            <div className="cb-issued-row">
              <span className="cb-issued-label">AppId</span>
              <span className="cb-issued-value">{appId}</span>
              <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyText(appId)} />
            </div>
            <div className="cb-issued-row">
              <span className="cb-issued-label">Secret</span>
              <span className="cb-issued-value">{secret}</span>
              <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyText(secret)} />
            </div>
            <div className="cb-issued-warn">{i('签发Secret警告')}</div>
          </div>
        ),
        okText: i('我已保存'),
      });
    }).catch(() => {
      message.error(i('获取Secret失败'));
    });
  };

  // ========== 管理凭证弹窗 ==========
  const openManageModal = (type: CallbackAddressType.MessageType) => {
    setManageModalType(type);
    setResetAppIdResult(null);
    setResetSecretResult(null);
    setResettingAppId(false);
    setResettingSecret(false);
  };

  const closeManageModal = () => {
    setManageModalType(null);
    setResetAppIdResult(null);
    setResetSecretResult(null);
    // 关闭后刷新列表获取最新 AppId
    runGetList();
  };

  const handleResetAppId = async () => {
    if (!manageModalType) return;
    setResettingAppId(true);
    try {
      const res = await resetCallbackAppId({ messageType: manageModalType });
      if (res?.code === 0) {
        const newAppId = res?.data?.callbackAppId || '';
        setResetAppIdResult(newAppId);
        message.success(i('重置AppId成功'));
      } else {
        message.error(ERROR_CODE_MAP[res?.code] || i('重置AppId失败'));
      }
    } catch (e) {
      message.error(i('重置AppId失败'));
    } finally {
      setResettingAppId(false);
    }
  };

  const handleResetSecret = async () => {
    if (!manageModalType) return;
    setResettingSecret(true);
    try {
      const res = await resetCallbackSecret({ messageType: manageModalType });
      if (res?.code === 0) {
        const newSecret = res?.data?.callbackSecret || '';
        setResetSecretResult(newSecret);
        if (manageModalType) {
          setJustIssuedSecrets((prev) => ({ ...prev, [manageModalType]: newSecret }));
        }
        message.success(i('重置Secret成功'));
      } else {
        message.error(ERROR_CODE_MAP[res?.code] || i('重置Secret失败'));
      }
    } catch (e) {
      message.error(i('重置Secret失败'));
    } finally {
      setResettingSecret(false);
    }
  };

  // ========== 渲染：推送账号 ==========
  const renderAdminPanel = () => {
    const adminMeta = admin?.personId
      ? i('推送账号已设置说明', { name: admin.nickname || admin.account || admin.personId })
      : i('推送账号未设置说明');

    return (
      <div className="cb-panel">
        <div className="cb-panel-title">
          <span>{i('推送账号')}</span>
          {adminIsSet ? (
            <Tag color="processing">{i('已设置')}</Tag>
          ) : (
            <Tag>{i('未设置')}</Tag>
          )}
        </div>
        <div className="cb-row">
          <div className="cb-label"><span className="cb-required">*</span>{i('推送账号')}</div>
          <div className="cb-field cb-field-limit">
            <InfiniteScrollSelect
              showSearch
              value={adminDraft || undefined}
              disabled={!canChangeAdmin}
              placeholder={i('请选择推送账号')}
              options={accounts}
              optionKey="personId"
              valueKey="nickname"
              messageKey="account"
              pageSize={20}
              total={accountTotal}
              loadMore={loadDevelopers}
              onChange={(v) => setAdminDraft(v)}
              style={{ width: '100%' }}
            />
          </div>
          <Button
            type="primary"
            loading={adminSaving}
            disabled={!canChangeAdmin}
            onClick={handleSaveAdmin}
          >
            {i('保存推送账号')}
          </Button>
        </div>
        <div className="cb-admin-meta">
          <div>
            <span>{i('创建人')}：<b>{admin?.nickname || admin?.account || admin?.creator || '—'}</b></span>
            <span className="cb-admin-meta-gap">{i('创建时间')}：<b>{admin?.createdAt || '—'}</b></span>
          </div>
          <div className="cb-admin-scope">{adminMeta}</div>
        </div>
      </div>
    );
  };

  // ========== 渲染：凭证区域 ==========
  const renderCredential = (type: CallbackAddressType.MessageType) => {
    const config = getConfig(type);
    const hasAppId = !!config?.callbackAppId;
    const justSecret = justIssuedSecrets[type];

    return (
      <div className="cb-cred">
        <div className="cb-cred-head">
          <span>{i('回调凭证')}</span>
          {hasAppId && (
            <Button
              type="link"
              size="small"
              disabled={!canEdit}
              onClick={() => openManageModal(type)}
            >
              {i('管理凭证')}
            </Button>
          )}
        </div>
        {!hasAppId ? (
          <p className="cb-cred-empty">{i('凭证未签发提示')}</p>
        ) : (
          <div className="cb-cred-issued">
            <div className="cb-cred-line">
              <span className="cb-cred-key">AppId</span>
              <span className="cb-cred-val cb-mono">{config.callbackAppId}</span>
              <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyText(config.callbackAppId!)} />
            </div>
            <div className="cb-cred-line">
              <span className="cb-cred-key">Secret</span>
              <span className="cb-cred-val cb-mono">
                {justSecret || i('Secret已隐藏提示')}
              </span>
              {justSecret && (
                <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyText(justSecret)} />
              )}
            </div>
            {justSecret && (
              <div className="cb-cred-warn">
                {i('Secret仅展示一次警告')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ========== 渲染：卡片操作按钮 ==========
  const renderActions = (type: CallbackAddressType.MessageType) => {
    const registered = isRegistered(type);
    const editing = isCardEditing(type);

    if (!registered || editing) {
      return (
        <div className="cb-actions">
          <Button type="primary" loading={saving} disabled={!canEdit} onClick={() => handleSave(type)}>
            {i('保存')}
          </Button>
          {registered && (
            <Button onClick={handleCancel}>{i('取消')}</Button>
          )}
        </div>
      );
    }
    return (
      <div className="cb-actions">
        <Button type="primary" disabled={!canEdit} onClick={() => handleModify(type)}>
          {i('修改')}
        </Button>
        <Button disabled={!canEdit} onClick={() => handleClear(type)}>
          {i('清空')}
        </Button>
      </div>
    );
  };

  // ========== 渲染：回调卡片 ==========
  const renderCard = (type: CallbackAddressType.MessageType) => {
    const isEvent = type === MSG_TYPE_INCIDENT;
    const config = getConfig(type);
    const editable = isCardEditable(type);
    const url = isEvent ? drafts.incidentUrl : drafts.taskUrl;
    const lang = drafts.incidentLang;
    const enabled = isEvent ? drafts.incidentEnabled : drafts.taskEnabled;
    const testResult = testResults[type];

    return (
      <div className="cb-card">
        <div className="cb-card-head">
          <div className="cb-card-title">{cardLabel(type)}</div>
          {config?.registered ? (
            <Tag color="processing">{i('已登记')}</Tag>
          ) : (
            <Tag>{i('未登记')}</Tag>
          )}
        </div>

        {config?.disabledBySystem && (
          <Alert
            className="cb-system-alert"
            type="error"
            showIcon
            message={i('系统已关闭推送提示')}
          />
        )}

        {renderCredential(type)}

        <div className="cb-row">
          <div className="cb-label">{i('回调地址')}</div>
          <div className="cb-field">
            <Input
              value={url}
              placeholder={i('例如 https://example.com/event')}
              disabled={!editable || !canEdit}
              onChange={(e) => setField(isEvent ? 'incidentUrl' : 'taskUrl', e.target.value)}
            />
            <Button
              loading={testingType === type}
              disabled={!canEdit}
              onClick={() => handleTest(type)}
            >
              {i('测试')}
            </Button>
          </div>
        </div>
        {testResult && (
          <div className={`cb-test-msg ${testResult.ok ? 'cb-test-ok' : 'cb-test-err'}`}>
            {testResult.msg}
          </div>
        )}

        {isEvent && (
          <div className="cb-row">
            <div className="cb-label">{i('事件语言')}</div>
            <div className="cb-field">
              <Select
                value={lang || undefined}
                placeholder={i('请选择事件语言')}
                options={LANGUAGE_OPTIONS}
                disabled={!editable || !canEdit}
                onChange={(v) => setField('incidentLang', v)}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        <div className="cb-row">
          <div className="cb-label">{i('推送开关')}</div>
          <div className="cb-field">
            <Switch
              checked={enabled === 1}
              disabled={!editable || !canEdit}
              onChange={(checked) =>
                setField(
                  isEvent ? 'incidentEnabled' : 'taskEnabled',
                  checked ? 1 : 0
                )
              }
            />
          </div>
        </div>

        {renderActions(type)}
      </div>
    );
  };

  // ========== 渲染：管理凭证弹窗 ==========
  const renderManageModal = () => {
    if (!manageModalType) return null;
    const config = getConfig(manageModalType);
    if (!config?.callbackAppId) return null;

    return (
      <Modal
        title={i('管理凭证标题', { type: cardLabel(manageModalType) })}
        open={!!manageModalType}
        onCancel={closeManageModal}
        footer={<Button onClick={closeManageModal}>{i('关闭')}</Button>}
        width={520}
      >
        {/* 重置 AppId */}
        <div className="cb-manage-row">
          <div className="cb-manage-info">
            <strong>{i('重置AppId')}</strong>
            <p>{i('重置AppId说明')}</p>
          </div>
          <Button
            loading={resettingAppId}
            disabled={!!resetAppIdResult}
            onClick={handleResetAppId}
          >
            {resetAppIdResult ? i('已重置') : i('重置')}
          </Button>
        </div>
        {resetAppIdResult && (
          <div className="cb-manage-result">
            <span className="cb-manage-result-label">AppId</span>
            <span className="cb-manage-result-value cb-mono">{resetAppIdResult}</span>
            <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyText(resetAppIdResult)} />
          </div>
        )}

        {/* 重置 Secret */}
        <div className="cb-manage-row">
          <div className="cb-manage-info">
            <strong>{i('重置Secret')}</strong>
            <p>{i('重置Secret说明')}</p>
          </div>
          <Button
            loading={resettingSecret}
            disabled={!!resetSecretResult}
            onClick={handleResetSecret}
          >
            {resetSecretResult ? i('已重置') : i('重置')}
          </Button>
        </div>
        {resetSecretResult && (
          <div className="cb-manage-result">
            <span className="cb-manage-result-label">Secret</span>
            <span className="cb-manage-result-value cb-mono">{resetSecretResult}</span>
            <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyText(resetSecretResult)} />
          </div>
        )}
      </Modal>
    );
  };

  // ========== 主渲染 ==========
  return (
    <div className="cb-page">
      <div className="cb-header">
        <h1 className="cb-title">{i('企业回调地址')}</h1>
        <p className="cb-hint">
          {i('企业回调地址页面说明')}
          {' '}
          <a
            href={`${(generateConfig() as any).developerUrl}/${getLocale() === 'zh-CN' ? 'zh_CN' : 'en_US'}/Robot%20Push%20Service/Callback%20Signature`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {i('回调验签')}
          </a>
        </p>
      </div>

      {renderAdminPanel()}

      <div className="cb-cards">
        {renderCard(MSG_TYPE_INCIDENT)}
        {renderCard(MSG_TYPE_TASK)}
      </div>

      {renderManageModal()}
    </div>
  );
}

export default CallbackAddress;
